/**
 * ReopenDialog — admin extends the submission window for a quiz or
 * assignment, optionally only for selected students. Calls
 * POST /school/tenants/{tenantId}/assessments/reopen which audit-logs
 * every reopen and supports per-student overrides without touching the
 * base window.
 *
 * Designed to drop into any quiz/assignment detail or row action. Small,
 * controlled — opener owns the open/close state.
 */
import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Wand2, Loader2 } from 'lucide-react';

export default function ReopenDialog({
  open,
  onOpenChange,
  tenantId,
  kind,            // 'quiz' | 'assignment'
  itemId,
  itemTitle = '',
  students = [],   // optional [{id, name}] for per-student picker
  onSuccess,
}) {
  const [availableUntil, setAvailableUntil] = useState('');
  const [graceMinutes, setGraceMinutes] = useState(0);
  const [additionalAttempts, setAdditionalAttempts] = useState(0);
  const [reason, setReason] = useState('');
  const [scope, setScope] = useState('class'); // 'class' | 'students'
  const [picked, setPicked] = useState({});

  const pickedIds = useMemo(
    () => Object.keys(picked).filter((k) => picked[k]),
    [picked],
  );

  const mutation = useMutation({
    mutationFn: () => api.post(`/school/tenants/${tenantId}/assessments/reopen`, {
      kind,
      item_id: itemId,
      student_ids: scope === 'students' ? pickedIds : [],
      available_until: availableUntil,
      grace_minutes: graceMinutes,
      additional_attempts: kind === 'quiz' ? additionalAttempts : 0,
      reason,
    }),
    onSuccess: (r) => {
      toast.success(
        `Reopened ${r.data.scope === 'class' ? 'class-wide' : `for ${r.data.affected} student${r.data.affected !== 1 ? 's' : ''}`} until ${new Date(availableUntil).toLocaleString()}`,
      );
      onOpenChange(false);
      // Reset for next time
      setAvailableUntil(''); setGraceMinutes(0); setAdditionalAttempts(0);
      setReason(''); setScope('class'); setPicked({});
      onSuccess?.();
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Reopen failed'),
  });

  const submit = () => {
    if (!availableUntil) return toast.error('Pick a new close date/time');
    if (scope === 'students' && pickedIds.length === 0) return toast.error('Tick at least one student');
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reopen-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-emerald-600" />
            Reopen {kind} {itemTitle ? `— ${itemTitle}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          {/* Scope toggle */}
          {students && students.length > 0 && (
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
              {['class', 'students'].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={`px-3 py-1 text-xs rounded-md ${scope === s ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
                  data-testid={`reopen-scope-${s}`}
                >
                  {s === 'class' ? 'Whole class' : `Selected students`}
                </button>
              ))}
            </div>
          )}

          {scope === 'students' && (
            <div>
              <Label className="text-xs text-slate-500">Students ({pickedIds.length} selected)</Label>
              <div className="max-h-40 overflow-y-auto border rounded mt-1 divide-y" data-testid="reopen-students">
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!picked[s.id]}
                      onChange={(e) => setPicked({ ...picked, [s.id]: e.target.checked })}
                      data-testid={`reopen-student-${s.id}`}
                    />
                    <span>{s.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">New close date/time *</Label>
              <Input
                type="datetime-local"
                value={availableUntil}
                onChange={(e) => setAvailableUntil(e.target.value)}
                data-testid="reopen-until"
              />
            </div>
            <div>
              <Label className="text-xs">Grace minutes</Label>
              <Input
                type="number"
                min="0"
                value={graceMinutes}
                onChange={(e) => setGraceMinutes(parseInt(e.target.value, 10) || 0)}
              />
            </div>
            {kind === 'quiz' && (
              <div>
                <Label className="text-xs">Additional attempts</Label>
                <Input
                  type="number"
                  min="0"
                  value={additionalAttempts}
                  onChange={(e) => setAdditionalAttempts(parseInt(e.target.value, 10) || 0)}
                />
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Reason (audit-logged)</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this needs to be reopened…"
              data-testid="reopen-reason"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={mutation.isPending}
            data-testid="reopen-submit"
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}
            Reopen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
